import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { getUploadRecords } from '../services/offlineStorage';
import { attachOriginalFile, deliverUpload, recoverOwnedUploads } from '../services/uploadQueue';
import { readSession, sessionIsCurrent, SESSION_CHANGED, STORAGE_CHANGED } from '../services/submissionSession';

const labels = { pending: 'Saved on this device', checking: 'Checking confirmation', 'awaiting-confirmation': 'Awaiting confirmation',
  'needs-file': 'Original file needed', 'needs-review': 'Review required', 'awaiting-call': 'Call awaiting confirmation', accepted: 'Confirmed' };

export default function UploadRecoveryPanel({ ownerUserId, onAccepted }) {
  const [rows, setRows] = useState([]), [error, setError] = useState(''), [busy, setBusy] = useState(null);
  const [receipts, setReceipts] = useState(false), [limit, setLimit] = useState(20);
  const mounted = useRef(true), generation = useRef(0), checking = useRef(false);
  const load = useCallback(async () => {
    if (!mounted.current) return;
    const session = readSession(), version = ++generation.current;
    if (session?.ownerUserId !== ownerUserId) { setRows([]); return; }
    try {
      const saved = await getUploadRecords(ownerUserId);
      if (mounted.current && version === generation.current && sessionIsCurrent(session)) setRows(saved.sort((a, b) => b.localId - a.localId));
    } catch (err) { if (mounted.current && sessionIsCurrent(session)) setError(err.message); }
  }, [ownerUserId]);
  const reconcile = useCallback(async () => {
    const session = readSession();
    if (checking.current || session?.ownerUserId !== ownerUserId || session.expired || !navigator.onLine) return;
    checking.current = true;
    try {
      const results = await recoverOwnedUploads(session.token);
      if (mounted.current && sessionIsCurrent(session) && results.some(result => result.accepted)) onAccepted?.();
    } catch (err) { if (mounted.current && sessionIsCurrent(session)) setError(err.message); }
    finally { checking.current = false; await load(); }
  }, [ownerUserId, onAccepted, load]);
  useEffect(() => {
    mounted.current = true; load(); reconcile();
    const changed = () => { load(); };
    const timer = setInterval(reconcile, 30000);
    window.addEventListener(STORAGE_CHANGED, changed); window.addEventListener(SESSION_CHANGED, changed);
    window.addEventListener('storage', changed); window.addEventListener('online', reconcile);
    return () => { mounted.current = false; clearInterval(timer);
      window.removeEventListener(STORAGE_CHANGED, changed); window.removeEventListener(SESSION_CHANGED, changed);
      window.removeEventListener('storage', changed); window.removeEventListener('online', reconcile); };
  }, [load, reconcile]);
  async function run(row, sendFile = false, file = null) {
    const session = readSession();
    if (!session || session.ownerUserId !== ownerUserId || busy !== null) return;
    setBusy(row.localId); setError('');
    try {
      if (file) await attachOriginalFile(row.localId, file, session.token);
      const result = await deliverUpload(row.localId, session.token, { sendFile });
      if (mounted.current && sessionIsCurrent(session) && result.accepted) onAccepted?.();
    } catch (err) { if (mounted.current && sessionIsCurrent(session)) setError(err.message); }
    finally { if (mounted.current) { setBusy(null); await load(); } }
  }
  const pending = rows.filter(row => row.status !== 'accepted'), accepted = rows.filter(row => row.status === 'accepted');
  const visible = receipts ? rows : pending;
  return <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="h6">Upload recovery {pending.length > 0 ? `(${pending.length} pending)` : ''}</Typography>
      {accepted.length > 0 && <Button onClick={() => { setReceipts(!receipts); setLimit(20); }}>{receipts ? 'Hide confirmed uploads' : `Show confirmed uploads (${accepted.length})`}</Button>}
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Upload details are saved for your account on this device. Keep the original recordings. After a browser restart, choose the same file to continue. Checking confirmation does not resend footage.
    </Typography>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {visible.length === 0 && <Typography variant="body2">No pending uploads for this account.</Typography>}
    <Stack spacing={2}>
      {visible.slice(0, limit).map(row => <Box key={row.localId} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, minWidth: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ overflowWrap: 'anywhere' }}>{row.file.name}</Typography>
          <Chip size="small" label={labels[row.status] || 'Pending review'} color={row.status === 'accepted' ? 'success' : row.status === 'needs-review' ? 'warning' : 'default'} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {row.receipt?.incident?.cadId || (row.payload.incidentId ? `Call ${row.payload.incidentId}` : 'Saved pending call')} · {row.payload.cameraType} · {(row.file.size / 1048576).toFixed(1)} MB
        </Typography>
        {row.lastError && <Typography role="status" variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>{row.lastError}</Typography>}
        {row.status !== 'accepted' && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
          <Button variant="outlined" disabled={busy !== null} onClick={() => run(row)}>Check confirmation</Button>
          {row.status !== 'needs-review' && <>
            <Button variant="contained" disabled={busy !== null} onClick={() => run(row, true)}>Continue upload</Button>
            <Button component="label" disabled={busy !== null}>Choose original &amp; continue
              <input hidden type="file" accept="video/*,.mts,.m2ts" onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; if (file) run(row, true, file); }} />
            </Button>
          </>}
        </Stack>}
      </Box>)}
    </Stack>
    {visible.length > limit && <Button sx={{ mt: 2 }} onClick={() => setLimit(value => value + 20)}>Show more uploads ({visible.length - limit} remaining)</Button>}
  </Paper>;
}
